import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { API_BASE } from "@/api/client";
import { toast } from "sonner";
import { Upload, FolderOpen, File, CheckCircle2, XCircle, Clock, Eye, Trash2, Link2, Plus, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const STATUS_CONFIG = {
  uploaded: { label: "Uploaded", class: "bg-slate-100 text-slate-600" },
  under_review: { label: "Under Review", class: "bg-amber-50 text-amber-700" },
  verified: { label: "Verified", class: "bg-emerald-50 text-emerald-700" },
  rejected: { label: "Rejected", class: "bg-red-50 text-red-700" }
};

const EVIDENCE_TYPES = ["utility_bill", "fuel_invoice", "production_record", "feasibility_study", "engineering_document", "meter_reading", "invoice", "report", "other"];

export default function EvidenceVault() {
  const { currentOrg } = useAuth();
  const [evidence, setEvidence] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: "", type: "utility_bill", assessment_id: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef();

  useEffect(() => {
    loadEvidence();
    loadAssessments();
  }, [currentOrg]);

  const loadEvidence = async () => {
    setLoading(true);
    try {
      const q = currentOrg ? `?org_id=${currentOrg.id}` : "";
      const res = await client.get(`/evidence${q}`);
      setEvidence(res.data);
    } catch {}
    setLoading(false);
  };

  const loadAssessments = async () => {
    try {
      const q = currentOrg ? `?org_id=${currentOrg.id}` : "";
      const res = await client.get(`/assessments${q}`);
      setAssessments(res.data);
    } catch {}
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) { toast.error("Please select a file"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("org_id", currentOrg?.id || "");
      formData.append("name", uploadForm.name || selectedFile.name);
      formData.append("type", uploadForm.type);
      if (uploadForm.assessment_id) formData.append("assessment_id", uploadForm.assessment_id);
      await client.post("/evidence/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Evidence uploaded successfully");
      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadForm({ name: "", type: "utility_bill", assessment_id: "" });
      loadEvidence();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await client.patch(`/evidence/${id}/status`, { status });
      toast.success(`Evidence marked as ${status}`);
      loadEvidence();
    } catch { toast.error("Failed to update status"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this evidence file?")) return;
    try { await client.delete(`/evidence/${id}`); toast.success("Evidence deleted"); loadEvidence(); }
    catch { toast.error("Failed to delete evidence"); }
  };

  const filtered = evidence.filter(e => {
    const matchSearch = e.name?.toLowerCase().includes(search.toLowerCase()) || e.type?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || e.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
          
      <PageHeader
        title="Evidence Vault"
        subtitle="Utility bills, invoices, and supporting documents for your climate data."
      />
        <button onClick={() => setShowUploadModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] transition-colors" data-testid="upload-evidence-btn">
          <Upload size={14} /> Upload Evidence
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Total Files", value: evidence.length },
          { label: "Verified", value: evidence.filter(e => e.status === "verified").length, color: "text-emerald-600" },
          { label: "Under Review", value: evidence.filter(e => e.status === "under_review").length, color: "text-amber-600" },
          { label: "Uploaded", value: evidence.filter(e => e.status === "uploaded").length }
        ].map(({ label, value, color }) => (
          <MetricCard key={label} label={label} value={value} color={color || "text-slate-900"} />
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search evidence..." className="w-full h-9 pl-8 pr-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" data-testid="evidence-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white">
          <option value="all">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Evidence Table */}
      <SectionCard noPadding>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="evidence-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">File</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden sm:table-cell">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Size</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                [...Array(3)].map((_, i) => <tr key={i}><td colSpan={5} className="px-5 py-4"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td></tr>)
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center">
                    <FolderOpen size={32} className="mx-auto mb-2 text-slate-300" />
                    <p className="text-sm text-slate-500">No evidence files yet</p>
                    <button onClick={() => setShowUploadModal(true)} className="mt-3 text-xs text-[#059669] hover:underline">Upload your first evidence file</button>
                  </td>
                </tr>
              ) : filtered.map(ev => (
                <tr key={ev.id} className="hover:bg-slate-50" data-testid={`evidence-row-${ev.id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <File size={14} className="text-slate-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{ev.name}</p>
                        <p className="text-xs text-slate-400">{ev.file_name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-xs text-slate-500 capitalize hidden sm:table-cell">{ev.type?.replace(/_/g, " ")}</td>
                  <td className="px-4 py-4 text-xs text-slate-500 hidden md:table-cell">{ev.file_size ? `${(ev.file_size / 1024).toFixed(0)} KB` : "—"}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[ev.status]?.class || "bg-slate-100 text-slate-600"}`}>
                      {STATUS_CONFIG[ev.status]?.label || ev.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {ev.status === "uploaded" && (
                        <button onClick={() => handleStatusUpdate(ev.id, "verified")} className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 transition-colors" data-testid={`verify-evidence-${ev.id}`}>
                          Verify
                        </button>
                      )}
                      {ev.status === "uploaded" && (
                        <button onClick={() => handleStatusUpdate(ev.id, "rejected")} className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors">
                          Reject
                        </button>
                      )}
                      <button onClick={() => handleDelete(ev.id)} className="text-slate-300 hover:text-red-500 transition-colors ml-1" data-testid={`delete-evidence-${ev.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Evidence</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 mt-2">
            <div
              className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#059669] hover:bg-emerald-50/30 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              data-testid="file-drop-zone"
            >
              <Upload size={24} className="mx-auto mb-2 text-slate-400" />
              <p className="text-sm font-medium text-slate-600">{selectedFile ? selectedFile.name : "Click to select file"}</p>
              <p className="text-xs text-slate-400 mt-1">PDF, Excel, Word, CSV, Images — max 10MB</p>
              <input ref={fileInputRef} type="file" className="hidden" onChange={e => setSelectedFile(e.target.files?.[0] || null)} accept=".pdf,.xlsx,.xls,.csv,.docx,.doc,.jpg,.jpeg,.png" />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Name</label>
              <input type="text" value={uploadForm.name} onChange={e => setUploadForm(f => ({ ...f, name: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" placeholder="e.g. Eskom Invoice Q1 2024" data-testid="evidence-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Type</label>
                <select value={uploadForm.type} onChange={e => setUploadForm(f => ({ ...f, type: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#059669]">
                  {EVIDENCE_TYPES.map(t => <option key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Link to Assessment</label>
                <select value={uploadForm.assessment_id} onChange={e => setUploadForm(f => ({ ...f, assessment_id: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#059669]">
                  <option value="">None</option>
                  {assessments.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setShowUploadModal(false)} className="flex-1 h-10 border border-slate-300 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={uploading || !selectedFile} className="flex-1 h-10 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="upload-submit-btn">
                {uploading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Uploading...</> : <><Upload size={14} /> Upload</>}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
